import React from "react";
import {
  FractionStore,
  MarkerStore,
  HighlightStore,
  Dispatcher,
  BookUtils,
  BookStore
} from "../../flux";
import {
  StickyList,
  SectionHeader,
  Isvg,
  Spinner,
  CardContainer,
  SearchField
} from "../../components";
import ManageResources from "../../../resources/manage-resources";
import MarkerDropdownItem from "./marker-dropdown-item";
import ChapterDropdownItem from "./chapter-dropdown-item";
import { VstDropdown } from "vstui";
import AccessibleSearchResults from "../../../accessability/accessibleSearchResults";

export const FILTER = {
  TYPE: {
    ALL: "FILTER_TYPE_ALL",
    NOTES: "FILTER_TYPE_NOTES",
    HIGHLIGHTS: "FILTER_TYPE_HIGHLIGHTS"
  },
  CREATOR: {
    ALL: "FILTER_CREATOR_ALL",
    ME: "FILTER_CREATOR_ME",
    OTHERS: "FILTER_CREATOR_OTHERS"
  },
  CHAPTER: {
    ALL: "FILTER_CHAPTER_ALL",
    ONE: "FILTER_CHAPTER_ONE"
  },
  HIGHLIGHTER: {
    ALL: "FILTER_HIGHLIGHTER_ALL",
    ONE: "FILTER_HIGHLIGHTER_ONE"
  }
};

export const SORT = {
  DATE: "SORT_DATE",
  AZ: "SORT_AZ",
  ZA: "SORT_ZA"
};

export const defaultFilters = () => ({
  [FILTER.TYPE.ALL]: true,
  [FILTER.TYPE.NOTES]: false,
  [FILTER.TYPE.HIGHLIGHTS]: false,
  [FILTER.CREATOR.ALL]: true,
  [FILTER.CREATOR.ME]: false,
  [FILTER.CREATOR.OTHERS]: false,
  [FILTER.CHAPTER.ALL]: true,
  [FILTER.CHAPTER.ONE]: false,
  [FILTER.HIGHLIGHTER.ALL]: true,
  [FILTER.HIGHLIGHTER.ONE]: false
});

/*
  Renders the notebook panel
    props:
    state:
*/
export default class Highlights extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filters: defaultFilters(),
      sort: SORT.AZ,
      findValue: "",
      cachedHighlights: [],
      highlights: [],
      subscribedHighlights: [],
      highlightsToLoad: 6,
      markers: [],
      subscribedMarkers: [],
      allMarkers: [],
      chapters: [],
      menuOpen: null,
      loading: true
    };

    // Dispatcher functions
    this._highlightsChange = this._highlightsChange.bind(this);
    this._markersChange = this._markersChange.bind(this);
    this._tocLoaded = this._tocLoaded.bind(this);

    this._filterByTypeAll = this._filterByType.bind(this, FILTER.TYPE.ALL);
    this._filterByTypeNotes = this._filterByType.bind(this, FILTER.TYPE.NOTES);
    this._filterByTypeHighlights = this._filterByType.bind(
      this,
      FILTER.TYPE.HIGHLIGHTS
    );

    this._filterByCreatorAll = this._filterByCreator.bind(
      this,
      FILTER.CREATOR.ALL
    );
    this._filterByCreatorMe = this._filterByCreator.bind(
      this,
      FILTER.CREATOR.ME
    );
    this._filterByCreatorOthers = this._filterByCreator.bind(
      this,
      FILTER.CREATOR.OTHERS
    );

    this._filterByHighlighterAll = this._filterByHighlighter.bind(
      this,
      FILTER.HIGHLIGHTER.ALL
    );
    this._filterByHighlighterOne = this._filterByHighlighter.bind(
      this,
      FILTER.HIGHLIGHTER.ONE
    );

    this._filterByChapterAll = this._filterByChapter.bind(
      this,
      FILTER.CHAPTER.ALL
    );
    this._filterByChapterOne = this._filterByChapter.bind(
      this,
      FILTER.CHAPTER.ONE
    );

    this._sortAZ = this._sort.bind(this, SORT.AZ);
    this._sortZA = this._sort.bind(this, SORT.ZA);
    this._sortDate = this._sort.bind(this, SORT.DATE);

    this.manageHighlighters = e =>
      ManageResources._manageHighlighters(e, null, this.props.isbn);
    this.manageSharing = e =>
      ManageResources._manageSharing(e, null, this.props.isbn);

    this.openFiltersMenu = this.openMenu.bind(this, "filters");
    this.openSettingsMenu = this.openMenu.bind(this, "settings");
    this._onScroll = this._onScroll.bind(this);
    this.closeMenu = this.closeMenu.bind(this);
    this._onFindFieldChange = this._onFindFieldChange.bind(this);
    this._resetFilters = this._resetFilters.bind(this);
    this._requestMoreHighlights = this._requestMoreHighlights.bind(this);
  }

  componentDidMount() {
    Dispatcher.setMaxListeners(Dispatcher.getMaxListeners() + 9);
    Dispatcher.bind(Dispatcher.HIGHLIGHTS_CHANGE, this._highlightsChange);
    Dispatcher.bind(Dispatcher.BOOK_SNIPPET_READY, this._highlightsChange);
    Dispatcher.bind(Dispatcher.MARKERS_CHANGE, this._markersChange);
    Dispatcher.bind(Dispatcher.BOOK_TOC_LOADED, this._tocLoaded);

    this._highlightsChange();
  }

  componentWillUnmount() {
    Dispatcher.unbind(Dispatcher.HIGHLIGHTS_CHANGE, this._highlightsChange);
    Dispatcher.unbind(Dispatcher.BOOK_SNIPPET_READY, this._highlightsChange);
    Dispatcher.unbind(Dispatcher.MARKERS_CHANGE, this._markersChange);
    Dispatcher.unbind(Dispatcher.BOOK_TOC_LOADED, this._tocLoaded);
    if (this.highlightThrottle) {
      clearTimeout(this.highlightThrottle);
    }
    Dispatcher.setMaxListeners(Dispatcher.getMaxListeners() - 9);
  }

  componentDidUpdate(prevProps, prevState) {
    this._rendered();
    if (
      this.state.highlights !== prevState.highlights ||
      this.state.cachedHighlights !== prevState.cachedHighlights
    ) {
      const newState = Object.assign(
        this._loadHighlighters(),
        this._loadChapters()
      );
      this.setState(newState);
    }
  }

  combinedHighlights() {
    return this.state.highlights.concat(this.state.subscribedHighlights);
  }

  _resetFilters() {
    this.setState(
      { filters: defaultFilters(), menuOpen: null },
      this._reloadHighlights
    );
  }

  _loadHighlighters() {
    let { markers, subscribedMarkers } = MarkerStore.getAll();
    const highlights = this.combinedHighlights();

    markers = markers.filter(({ globalId }) =>
      highlights.some(({ markerId }) => markerId === globalId)
    );

    subscribedMarkers = subscribedMarkers.filter(({ globalId }) =>
      highlights.some(({ markerId }) => markerId === globalId)
    );

    const allMarkers = markers.concat(subscribedMarkers);

    return { markers, subscribedMarkers, allMarkers };
  }

  _loadChapters() {
    const book = BookStore.get(this.props.isbn);
    let chapters = book.getTOC();
    if (!chapters) {
      return { chapters: [] };
    }
    if (chapters) {
      const highlights = this.combinedHighlights();
      chapters = chapters
        .filter(c => c.level == 1)
        .filter((chapter, index, chapters) =>
          highlights.some(highlight => {
            if (
              highlight.chapterTitle &&
              highlight.chapterTitle === chapter.title
            ) {
              // pdfs and their dumb stupid cfis that aren't right
              return true;
            }

            if (!highlight.cfi) {
              return false;
            }

            const ccValue = Jigsaw.CFIUtils.compareCFIs(
              highlight.cfi,
              chapter.cfi
            );
            if ((index === 0 && ccValue === -1) || ccValue === 0) {
              return true;
            }
            if (!chapters[index + 1]) {
              return ccValue === 1;
            }
            const ncvalue = Jigsaw.CFIUtils.compareCFIs(
              highlight.cfi,
              chapters[index + 1].cfi
            );
            if (ccValue === 1 && ncvalue === -1) {
              return true;
            }
          })
        );
    }
    return { chapters };
  }

  _highlightsChange() {
    let highlightsObj;
    const { startCFI, endCFI, rangeType } = this.props;
    if (startCFI && endCFI) {
      if (rangeType === "page_index") {
        highlightsObj = HighlightStore.getRangeByIndex(
          this.props.isbn,
          startCFI,
          endCFI
        );
      } else {
        highlightsObj = HighlightStore.getRange(
          this.props.isbn,
          startCFI,
          endCFI
        );
      }
    } else {
      highlightsObj = HighlightStore.getAll(this.props.isbn);
    }

    if (!highlightsObj.highlightsLoading) {
      this.setState(
        {
          highlights: highlightsObj.highlights,
          subscribedHighlights: highlightsObj.subscribedHighlights,
          loading: highlightsObj.highlightsLoading
        },
        this._reloadHighlights
      );
    }
  }

  _filterByChapter(filterType, title) {
    const filterValue = filterType === FILTER.CHAPTER.ALL || title;
    const filters = {
      [FILTER.CHAPTER.ALL]: false,
      [FILTER.CHAPTER.ONE]: false,
      [filterType]: filterValue
    };
    this.setState(
      state => ({
        filters: Object.assign(state.filters, filters)
      }),
      this._reloadHighlights
    );
    Fraction.Reporter.gaEvent("reader/panel/notebook/filter", filterType);
  }

  _filterByHighlighter(filterType, markerId = true) {
    const filters = {
      [FILTER.HIGHLIGHTER.ALL]: false,
      [FILTER.HIGHLIGHTER.ONE]: false,
      [filterType]: markerId
    };
    this.setState(
      state => ({
        filters: Object.assign(state.filters, filters)
      }),
      this._reloadHighlights
    );

    Fraction.Reporter.gaEvent("reader/panel/notebook/filter", filterType);
  }

  _filterByType(filterType) {
    const filters = {
      [FILTER.TYPE.ALL]: false,
      [FILTER.TYPE.NOTES]: false,
      [FILTER.TYPE.HIGHLIGHTS]: false,
      [filterType]: true
    };
    this.setState(
      state => ({
        filters: Object.assign(state.filters, filters)
      }),
      this._reloadHighlights
    );
    Fraction.Reporter.gaEvent("reader/panel/notebook/filter", filterType);
  }

  _filterByCreator(filterCreator) {
    const filters = {
      [FILTER.CREATOR.ALL]: false,
      [FILTER.CREATOR.ME]: false,
      [FILTER.CREATOR.OTHERS]: false,
      [filterCreator]: true
    };
    this.setState(
      state => ({
        filters: Object.assign(state.filters, filters)
      }),
      this._reloadHighlights
    );
    Fraction.Reporter.gaEvent("reader/panel/notebook/filter", filterCreator);
  }

  _sort(type) {
    this.setState(
      {
        sort: type,
        highlightsToLoad: 6
      },
      this._reloadHighlights
    );
    Fraction.Reporter.gaEvent("reader/panel/notebook/sort", type);
  }

  _onFindFieldChange(findValue) {
    this.setState({ findValue }, this._reloadHighlights);
    Fraction.Helpers.delayTimeout(
      () => {
        Fraction.Reporter.gaEvent("reader/panel/notebook/find", findValue);
      },
      2500,
      "notebook-find-highlight"
    );
  }

  _markersChange() {
    this.setState(this._loadHighlighters(), this._reloadHighlights);
  }

  _tocLoaded() {
    this.setState(this._loadChapters(), this._reloadHighlights);
  }

  _rendered() {
    this.loadingMoreHighlights = false;

    if (this.results) {
      var scrollableElement =
        this.results.querySelectorAll(".sticky-list > .sticky-list-body")[0] ||
        this.results.querySelectorAll(".notecard-list")[0];
      if (scrollableElement) {
        var notebookLoaderHeight =
          (this.notebookLoader ? this.notebookLoader.clientHeight : 0) + 4;
        if (
          scrollableElement.scrollHeight - notebookLoaderHeight <=
          scrollableElement.clientHeight
        ) {
          this._requestMoreHighlights();
        }
      }
    }
  }

  _matchesFindFilter(highlight) {
    var findValue = this.state.findValue.trim().toLowerCase();
    if (!findValue) {
      return true;
    }
    var highlightText = highlight.selectedText.toLowerCase();
    var noteText = highlight.hasNote ? highlight.noteText.toLowerCase() : "";
    const marker = MarkerStore.get(highlight.markerId);
    var markerLabel = marker ? marker.label.toLowerCase() : "";
    var lastModifiedString = highlight.lastModifiedString
      ? highlight.lastModifiedString.toLowerCase()
      : "";
    var userInitials = highlight.userInitials
      ? highlight.userInitials.toLowerCase()
      : "";

    // If the card doesn't contain all the words in the find filter, don't show it.
    const words = findValue.split(/\s+/);
    return words.every(
      word =>
        highlightText.indexOf(word) !== -1 ||
        noteText.indexOf(word) !== -1 ||
        markerLabel.indexOf(word) !== -1 ||
        lastModifiedString.indexOf(word) !== -1 ||
        userInitials.indexOf(word) !== -1
    );
  }

  // Returns an array of filtered and sorted highlights
  _reloadHighlights() {
    if (!this.combinedHighlights().length) {
      return;
    }

    const compareDate = (a, b) => {
      var dateA = new Date(a.lastModifiedAt);
      var dateB = new Date(b.lastModifiedAt);
      if (dateA < dateB) {
        return -1;
      }
      if (dateA > dateB) {
        return 1;
      }
      return 0;
    };

    // Different book types (epub, pdf, etc) have different types of data,
    // so we need to sort based off of what's available.
    const compareBookOrder = (a, b) => {
      // sort by cfi (epub)
      if (a.cfi) {
        return Jigsaw.CFIUtils.compareCFIs(a.cfi, b.cfi);
      }

      // sort by startPage, then by chapterOffset (picture books: pdf/pbk)
      if (a.startPage) {
        var startPageResult = a.startPage - b.startPage;
        if (startPageResult === 0) {
          return a.chapterOffset - b.chapterOffset;
        }
        return startPageResult;
      }

      // sort by chapterCfi, then by chapterOffset (dash)
      var chapterCfiResult = Jigsaw.CFIUtils.compareCFIs(
        a.chapterCfi,
        b.chapterCfi
      );
      if (chapterCfiResult === 0) {
        return a.chapterOffset - b.chapterOffset;
      }
      return chapterCfiResult;
    };

    const { filters, sort } = this.state;
    let highlights;

    if (filters[FILTER.CREATOR.ALL]) {
      highlights = this.combinedHighlights();
    } else if (filters[FILTER.CREATOR.ME]) {
      highlights = this.state.highlights;
    } else if (filters[FILTER.CREATOR.OTHERS]) {
      highlights = this.state.subscribedHighlights;
    }

    if (filters[FILTER.TYPE.NOTES]) {
      highlights = highlights.filter(highlight => highlight.hasNote);
    } else if (filters[FILTER.TYPE.HIGHLIGHTS]) {
      highlights = highlights.filter(highlight => !highlight.hasNote);
    }

    if (filters[FILTER.CHAPTER.ONE]) {
      highlights = highlights.filter(highlight => {
        return this._getChapterTitle(highlight) === filters[FILTER.CHAPTER.ONE];
      });
    }

    if (filters[FILTER.HIGHLIGHTER.ONE]) {
      highlights = highlights.filter(
        ({ markerId }) => markerId === filters[FILTER.HIGHLIGHTER.ONE]
      );
    }

    highlights = highlights.filter(highlight =>
      this._matchesFindFilter(highlight)
    );

    if (sort === SORT.DATE) {
      highlights.sort(compareDate).reverse();
    } else if (sort === SORT.AZ) {
      highlights.sort(compareBookOrder);
    } else if (sort === SORT.ZA) {
      highlights.sort(compareBookOrder).reverse();
    }

    this.setState({
      cachedHighlights: highlights,
      highlightsToLoad: Math.min(
        Math.max(highlights.length, 6),
        this.state.highlightsToLoad
      )
    });

    return highlights;
  }

  _getChapterTitle(highlight, chapters) {
    return highlight.cfi
      ? BookUtils.getChapterTitleForCFI(this.props.isbn, highlight.cfi, {
          maxLevel: 1,
          chapters
        })
      : highlight.chapterTitle;
  }

  _reviewMode() {
    Dispatcher.emit("Extension.Launch.review-mode", this.props.isbn);
  }

  _filteredResults() {
    if (this.state.findValue.trim().length) {
      return this.combinedHighlights();
    }
    return this.combinedHighlights().filter(highlight =>
      this._matchesFindFilter(highlight)
    );
  }

  renderResults() {
    if (this.combinedHighlights().length === 0) {
      // user doesn't have any highlights yet
      const notesDown = FractionStore.get()["notes-down"];
      return (
        <div className="null-screen">
          {!notesDown ? (
            Fraction.Brand.Graphics.watkins ? (
              <img
                alt=""
                className="watkins"
                src="/img/watkins_prop_note.svg"
              />
            ) : (
              <img
                alt=""
                className="watkins null"
                src="/img/null_notebook.svg"
              />
            )
          ) : Fraction.Brand.Graphics.watkins ? (
            <img alt="" className="watkins" src="/img/prop_laptop_no.svg" />
          ) : (
            <span />
          )}
          {!notesDown ? (
            <div className="null-screen-description">
              <div className="null-screen-title">
                {i18n.t("reader.notebook.start-filling")}
              </div>
              {i18n.t("reader.notebook.add-to-unlock")}
            </div>
          ) : (
            <div>{i18n.t("reader.notebook.notes-info-error")}</div>
          )}
        </div>
      );
    }
    const { cachedHighlights: highlights } = this.state;
    if (highlights.length === 0) {
      // user has highlights, but the current filter is hiding them all
      if (this._filteredResults().length === 0) {
        // there's no find results at all
        return (
          <div className="null-screen">
            {Fraction.Brand.Graphics.watkins && (
              <img alt="" className="watkins" src="/img/prop_laptop_dust.svg" />
            )}
            {i18n.t("filter.no-results-for-find", {
              term: this.state.findValue
            })}
          </div>
        );
      }

      // turning off filter would show find results
      if (this.state.findValue === "") {
        return (
          <div className="null-screen">
            {Fraction.Brand.Graphics.watkins && (
              <img alt="" className="watkins" src="/img/prop_laptop_dust.svg" />
            )}
            <h2>{i18n.t("filter.nothing-here")}</h2>
            <button
              className="noButton reset-filters"
              onClick={this._resetFilters}
            >
              {i18n.t("filter.reset-filters")}
            </button>
          </div>
        );
      }

      return (
        <div className="null-screen">
          {Fraction.Brand.Graphics.watkins && (
            <img alt="" className="watkins" src="/img/prop_laptop_dust.svg" />
          )}
          <h2>{i18n.t("filter.nothing-here")}</h2>
        </div>
      );
    }

    if (this.state.sort === SORT.AZ || this.state.sort === SORT.ZA) {
      // Creates a multi-dimensional array
      // where results[x][0] is a chapterTitle
      // and results[x][1] is an array of CardContainers for every highlight in that chapter
      let chapterTitle, currentChapterTitle;
      const results = highlights
        .reduce((acc, highlight) => {
          const currentChapterTitle = this._getChapterTitle(
            highlight,
            this.state.chapters
          );
          const cardContainer = (
            <CardContainer
              {...highlight}
              key={`highlight-${highlight.syncId}`}
              id={`highlight-${highlight.syncId}`}
              close={this.props.close}
            />
          );
          if (chapterTitle !== currentChapterTitle) {
            chapterTitle = currentChapterTitle;
            acc.push([chapterTitle, [cardContainer]]);
          } else {
            acc[acc.length - 1][1].push(cardContainer);
          }
          return acc;
        }, [])
        .map(chapterFauxTuple => {
          const title = chapterFauxTuple[0];
          const highlights = chapterFauxTuple[1];
          return {
            header: (
              <SectionHeader
                key={`notebook-section-header-${title}`}
                count={chapterFauxTuple[1].length}
                title={title}
              />
            ),
            onHeaderClick: this._requestMoreHighlights,
            body: {
              className: "notecard-list",
              contentList: highlights
            }
          };
        });
      return <StickyList listItems={results} />;
    }

    // Just return a list of highlights
    var remainingHighlights = highlights.slice(0, this.state.highlightsToLoad);
    return (
      <ol className="notecard-list noselect">
        {remainingHighlights.map(highlight => (
          <CardContainer
            {...highlight}
            key={`highlight-${highlight.syncId}`}
            close={this.props.close}
          />
        ))}
        {remainingHighlights.length >= this.state.highlightsToLoad && (
          <div
            ref={nl => {
              this.notebookLoader = nl;
            }}
            className="notebook-loader"
          >
            <Isvg src="/img/loader.svg" />
          </div>
        )}
      </ol>
    );
  }

  _onScroll(e) {
    document.dispatchEvent(new CustomEvent("vst-dropdown.close"));
    this._requestMoreHighlights(1000);
  }

  _requestMoreHighlights(throttleTimeoutLength) {
    throttleTimeoutLength = throttleTimeoutLength || 1;
    if (
      this.state.highlightsToLoad < this.state.cachedHighlights.length &&
      !this.loadingMoreHighlights
    ) {
      this.highlightThrottle = Fraction.Helpers.throttle(
        () => {
          for (
            var i = this.state.highlightsToLoad - 3;
            i < this.state.highlightsToLoad - 1;
            i++
          ) {
            var highlight = this.state.cachedHighlights[i];
            if (highlight) {
              if (
                (this.notebookLoader &&
                  Fraction.Helpers.isScrolledIntoView(this.notebookLoader)) ||
                Fraction.Helpers.isScrolledIntoView(
                  document.getElementById(`notecard_${highlight.syncId}`)
                )
              ) {
                this.loadingMoreHighlights = true;
                this.setState({
                  highlightsToLoad: this.state.highlightsToLoad + 6
                });
                break;
              }
            }
          }
        },
        15,
        throttleTimeoutLength,
        "scroll-notebook"
      );
    }
  }

  openMenu(menu) {
    this.setState({ menuOpen: menu });
  }

  closeMenu() {
    this.setState({ menuOpen: null });
  }

  renderSettingsMenu() {
    if (
      !this.props.hideSettings &&
      (Fraction.Brand.Sharing.enabled || Fraction.Brand.Menu.highlighters)
    ) {
      const target = (
        <button className="vst-dropdown-target noButton">
          <span className="offScreen">{i18n.t("reader.panels.settings")}</span>
          <Isvg
            src="/img/settings.svg"
            className={this.state.menuOpen === "settings" ? "open" : ""}
          />
        </button>
      );
      const dropdownItems = [];
      if (Fraction.Brand.Menu.highlighters) {
        dropdownItems.push(
          <VstDropdown.Item
            key="mange-highlighters-item"
            onClick={this.manageHighlighters}
          >
            {i18n.t("highlighters.manage")}
          </VstDropdown.Item>
        );
      }
      if (Fraction.Brand.Sharing.enabled) {
        dropdownItems.push(
          <VstDropdown.Item
            key="manage-sharing-item"
            onClick={this.manageSharing}
          >
            {i18n.t("sharing.manage")}
          </VstDropdown.Item>
        );
      }
      return (
        <VstDropdown
          vertical="bottom"
          horizontal="right"
          target={target}
          onOpen={this.openSettingsMenu}
          onClose={this.closeMenu}
        >
          {dropdownItems}
        </VstDropdown>
      );
    }
  }

  open() {
    const df = defaultFilters();
    const keys = Object.keys(df);
    const { filters } = this.state;
    return (
      this.state.menuOpen === "filters" ||
      keys.some(key => filters[key] !== df[key])
    );
  }

  renderFiltersMenu() {
    const filters = this.state.filters;
    const target = (
      <button className="vst-dropdown-target noButton">
        <span className="offScreen">{i18n.t("filter.filters")}</span>
        <Isvg src="/img/filter.svg" className={this.open() ? "open" : ""} />
      </button>
    );
    return (
      <VstDropdown
        className="vst-dropdown-highlights-filters"
        vertical="bottom"
        horizontal="right"
        headerTitle={i18n.t("base.filter")}
        subheaderTitle={i18n.t("base.filter")}
        headerBackButton={<Isvg src="img/arrow_white_left.svg" />}
        isMobile={Fraction.Helpers.isSmallScreen()}
        target={target}
        onOpen={this.openFiltersMenu}
        onClose={this.closeMenu}
      >
        <VstDropdown.Item role="subheader">
          {i18n.t("filter.created-by")}
        </VstDropdown.Item>
        <VstDropdown.Item
          onClick={this._filterByCreatorAll}
          selected={filters[FILTER.CREATOR.ALL]}
        >
          {i18n.t("filter.all")}
        </VstDropdown.Item>
        <VstDropdown.Item
          onClick={this._filterByCreatorMe}
          selected={filters[FILTER.CREATOR.ME]}
        >
          {i18n.t("filter.me")}
        </VstDropdown.Item>
        <VstDropdown.Item
          onClick={this._filterByCreatorOthers}
          selected={filters[FILTER.CREATOR.OTHERS]}
        >
          {i18n.t("filter.others")}
        </VstDropdown.Item>

        <VstDropdown.Item role="subheader">
          {i18n.t("filter.view")}
        </VstDropdown.Item>

        <VstDropdown.Item
          onClick={this._filterByTypeAll}
          selected={filters[FILTER.TYPE.ALL]}
        >
          {i18n.t("filter.notes-highlights")}
        </VstDropdown.Item>
        <VstDropdown.Item
          onClick={this._filterByTypeNotes}
          selected={filters[FILTER.TYPE.NOTES]}
        >
          {i18n.t("filter.notes")}
        </VstDropdown.Item>
        <VstDropdown.Item
          onClick={this._filterByTypeHighlights}
          selected={filters[FILTER.TYPE.HIGHLIGHTS]}
        >
          {i18n.t("filter.highlights")}
        </VstDropdown.Item>

        <VstDropdown.Item role="subheader">
          {i18n.t("sorting.sorted-by")}
        </VstDropdown.Item>
        <VstDropdown.Item
          onClick={this._sortAZ}
          selected={this.state.sort === SORT.AZ}
        >
          {i18n.t("sorting.book-order-az")}
        </VstDropdown.Item>
        <VstDropdown.Item
          onClick={this._sortZA}
          selected={this.state.sort === SORT.ZA}
        >
          {i18n.t("sorting.book-order-za")}
        </VstDropdown.Item>
        <VstDropdown.Item
          onClick={this._sortDate}
          selected={this.state.sort === SORT.DATE}
        >
          {i18n.t("sorting.recent-activity")}
        </VstDropdown.Item>

        <VstDropdown.Item role="subheader" hidden={this.props.basicFilters}>
          {i18n.t("filter.view-by-chapter")}
        </VstDropdown.Item>
        <VstDropdown.Item
          role="select"
          text={i18n.t("filter.chapters")}
          hidden={this.props.basicFilters}
          headerTitle={i18n.t("filter.chapters")}
          headerBackButton={<Isvg src="img/arrow_white_left.svg" />}
          selected={!!filters[FILTER.CHAPTER.ONE]}
        >
          <VstDropdown.Item
            onClick={this._filterByChapterAll}
            selected={filters[FILTER.CHAPTER.ALL]}
          >
            {i18n.t("filter.all-chapters")}
          </VstDropdown.Item>

          {this.state.chapters.map(({ cfi, title }, i) => (
            <ChapterDropdownItem
              key={`${title}-${i}`}
              select={this._filterByChapterOne}
              selected={filters[FILTER.CHAPTER.ONE] === title}
              title={title}
            />
          ))}
        </VstDropdown.Item>

        <VstDropdown.Item role="subheader" hidden={this.props.basicFilters}>
          {i18n.t("filter.view-by-highlighter")}
        </VstDropdown.Item>
        <VstDropdown.Item
          role="select"
          text={i18n.t("filter.highlights")}
          hidden={this.props.basicFilters}
          className="marker-filters"
          headerTitle={i18n.t("filter.highlights")}
          headerBackButton={<Isvg src="img/arrow_white_left.svg" />}
          selected={!!filters[FILTER.HIGHLIGHTER.ONE]}
        >
          <VstDropdown.Item
            onClick={this._filterByHighlighterAll}
            selected={filters[FILTER.HIGHLIGHTER.ALL]}
          >
            {i18n.t("filter.all-highlighters")}
          </VstDropdown.Item>

          {this.state.allMarkers.map((marker, i) => (
            <MarkerDropdownItem
              key={`marker-dropdown-item-${marker.globalId}`}
              marker={marker}
              select={this._filterByHighlighterOne}
              selected={filters[FILTER.HIGHLIGHTER.ONE] === marker.globalId}
            />
          ))}
        </VstDropdown.Item>
      </VstDropdown>
    );
  }

  renderStatusBar() {
    const { cachedHighlights } = this.state;
    if (cachedHighlights.length > 0) {
      return (
        <div className="filters">
          <AccessibleSearchResults 
            className="result"
            result={ i18n.t("filter.notes-highlights-amount", { 
              amount: cachedHighlights.length }
            )} 
          />
          <div className="menus">
            {this.renderSettingsMenu()}
            {this.renderFiltersMenu()}
          </div>
        </div>
      );
    }
  }

  renderSearchFilters() {
    if (this.state.findValue) {
      const filters = this.state.filters;
      return (
        <div className="search-filter">
          <div className="outline">
            <button
              aria-pressed={filters[FILTER.TYPE.ALL] ? "true" : "false"}
              className={filters[FILTER.TYPE.ALL] ? "active" : ""}
              onClick={this._filterByTypeAll}
            >
              {i18n.t("filter.all")}
            </button>
            <button
              aria-pressed={filters[FILTER.TYPE.HIGHLIGHTS] ? "true" : "false"}
              className={filters[FILTER.TYPE.HIGHLIGHTS] ? "active" : ""}
              onClick={this._filterByTypeHighlights}
            >
              {i18n.t("filter.highlights")}
            </button>
            <button
              aria-pressed={filters[FILTER.TYPE.NOTES] ? "true" : "false"}
              className={filters[FILTER.TYPE.NOTES] ? "active" : ""}
              onClick={this._filterByTypeNotes}
            >
              {i18n.t("filter.notes")}
            </button>
          </div>
        </div>
      );
    }
  }

  renderSearch() {
    if (this.combinedHighlights().length > 0) {
      return (
        <div className="search-header">
          <SearchField
            placeholder={i18n.t("reader.notebook.find-placeholder")}
            onChange={this._onFindFieldChange}
            value={this.state.findValue}
            incremental
          />
        </div>
      );
    }
  }

  render() {
    if (!this.state.loading) {
      return (
        <div className="notes-highlights">
          {this.renderSearch()}
          {this.renderSearchFilters()}
          {this.renderStatusBar()}
          <div
            ref={r => {
              this.results = r;
            }}
            className={`results notebook-review-mode noselect ${this.props.className}`}
            onScroll={this._onScroll}
          >
            {this.renderResults()}
          </div>
        </div>
      );
    }
    return <Spinner />;
  }
}
